import { useCallback, useEffect, useRef, useState } from 'react';
import {
  AlertCircle, Check, CheckCircle, ChevronDown, Copy, ExternalLink, Hash,
  Image as ImageIcon, Loader2, Pencil, Sparkles, Trash2, X, ZoomIn,
} from 'lucide-react';
import { labApi } from '@/lib/api';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { ImageLightbox } from './ImageLightbox';

// KREACJE REKLAM STATYCZNYCH: rekordy zmaterializowane z JSON pipeline'ów
// STATIC_* (katalog KREATOS #9/#10/#12). Każda kreacja ma shortcode
// (referencja dla przyszłego modułu Reklama), edytowalny prompt i obraz
// generowany przez gpt-image w workerze (podgląd w DB + kopia na Drive).

interface AdCreativeRecord {
  id: string;
  shortcode: string;
  name: string;
  schemaNumber: string | null;
  schemaName: string | null;
  funnelStage: string | null;
  format: string;
  prompt: string;
  onImageText: string | null;
  executorNote: string | null;
  carouselGroup: string | null;
  slideNumber: number | null;
  variantLabel: string | null;
  hypothesis: string | null;
  status: string;
  error: string | null;
  driveFileUrl: string | null;
  createdAt: string;
  updatedAt: string;
}

interface AdCreativesPanelProps {
  productId: string;
  contentId: string;
}

const POLL_INTERVAL_MS = 8000;

const FORMAT_RATIO: Record<string, string> = {
  '1:1': 'aspect-square',
  '4:5': 'aspect-[4/5]',
  '9:16': 'aspect-[9/16]',
};

export function AdCreativesPanel({ productId, contentId }: AdCreativesPanelProps) {
  const [creatives, setCreatives] = useState<AdCreativeRecord[]>([]);
  const [isOpen, setIsOpen] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState<AdCreativeRecord | null>(null);
  const [lightbox, setLightbox] = useState<{ src: string; alt: string } | null>(null);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [expandedPromptId, setExpandedPromptId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editPrompt, setEditPrompt] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const [queueingAll, setQueueingAll] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const loadCreatives = useCallback(async () => {
    try {
      const res = await labApi.getAdCreatives(productId, contentId);
      setCreatives(res.data.data || []);
    } catch (err) {
      console.error('Failed to load ad creatives:', err);
    }
  }, [productId, contentId]);

  useEffect(() => {
    void loadCreatives();
  }, [loadCreatives]);

  const hasPending = creatives.some(creative => ['PENDING', 'GENERATING'].includes(creative.status));
  useEffect(() => {
    if (!hasPending) {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = null;
      return;
    }
    pollRef.current = setInterval(() => void loadCreatives(), POLL_INTERVAL_MS);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = null;
    };
  }, [hasPending, loadCreatives]);

  const copyToClipboard = (key: string, value: string) => {
    navigator.clipboard.writeText(value);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  const handleGenerateOne = async (creative: AdCreativeRecord) => {
    try {
      await labApi.generateAdCreativeImage(productId, contentId, creative.id);
      await loadCreatives();
    } catch (err) {
      console.error('Failed to queue ad creative image:', err);
    }
  };

  const handleGenerateAll = async () => {
    setQueueingAll(true);
    try {
      await labApi.generateAllAdCreativeImages(productId, contentId);
      await loadCreatives();
    } catch (err) {
      console.error('Failed to queue all ad creative images:', err);
    } finally {
      setQueueingAll(false);
    }
  };

  const startEdit = (creative: AdCreativeRecord) => {
    setEditingId(creative.id);
    setEditPrompt(creative.prompt);
  };

  const saveEdit = async () => {
    if (!editingId) return;
    setSavingEdit(true);
    try {
      await labApi.updateAdCreative(productId, contentId, editingId, { prompt: editPrompt });
      setEditingId(null);
      await loadCreatives();
    } catch (err) {
      console.error('Failed to save ad creative prompt:', err);
    } finally {
      setSavingEdit(false);
    }
  };

  const readyCount = creatives.filter(creative => creative.status === 'READY').length;
  const queueableCount = creatives.filter(creative => ['DRAFT', 'FAILED'].includes(creative.status)).length;

  if (creatives.length === 0) return null;

  return (
    <div className="bg-surface-hover/30 rounded-lg border border-border overflow-hidden">
      <button
        onClick={() => setIsOpen(prev => !prev)}
        className="w-full flex items-center gap-2.5 px-3 py-2.5 text-left hover:bg-surface-hover/40 transition-colors"
      >
        <ImageIcon className="w-4 h-4 text-accent-tertiary shrink-0" />
        <span className="text-xs font-semibold text-text-primary">Kreacje</span>
        <span className="px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-surface-raised border border-border-subtle text-text-secondary">
          {readyCount}/{creatives.length}
        </span>
        {hasPending && <Loader2 className="w-3 h-3 animate-spin text-warning shrink-0" />}
        <ChevronDown className={`w-3.5 h-3.5 text-text-muted ml-auto shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="px-3 pb-3 space-y-2 border-t border-border-subtle pt-3">
          <div className="flex items-center gap-3">
            <button
              onClick={handleGenerateAll}
              disabled={queueingAll || queueableCount === 0}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-accent-tertiary bg-accent-tertiary/10 border border-accent-tertiary/20 rounded-lg hover:bg-accent-tertiary/15 transition-all disabled:opacity-40"
            >
              {queueingAll ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
              Generuj wszystkie obrazy{queueableCount > 0 ? ` (${queueableCount})` : ''}
            </button>
            <span className="text-[10px] text-text-muted">
              Prompt każdej kreacji możesz skopiować do dowolnego generatora albo wygenerować obraz w EcomFlow (gpt-image + packshot produktu jako referencja).
            </span>
          </div>

          <div className="space-y-2">
            {creatives.map(creative => {
              const imageUrl = labApi.getAdCreativeImageUrl(productId, contentId, creative.id, creative.updatedAt);
              const isBusy = ['PENDING', 'GENERATING'].includes(creative.status);
              const promptExpanded = expandedPromptId === creative.id;
              const isEditing = editingId === creative.id;
              return (
                <div key={creative.id} className="rounded-lg border border-border-subtle bg-surface-hover/30 p-2.5 flex gap-3">
                  <div className={`w-24 sm:w-28 shrink-0 ${FORMAT_RATIO[creative.format] || 'aspect-square'} rounded-md overflow-hidden bg-surface-hover relative group`}>
                    {creative.status === 'READY' ? (
                      <>
                        <img src={imageUrl} alt={creative.name} loading="lazy" className="w-full h-full object-cover" />
                        <button
                          onClick={() => setLightbox({ src: imageUrl, alt: creative.name })}
                          className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity"
                          title="Podgląd"
                          aria-label="Podgląd kreacji"
                        >
                          <ZoomIn className="w-4 h-4 text-white" />
                        </button>
                      </>
                    ) : (
                      <div className="w-full h-full flex flex-col items-center justify-center gap-1 px-1 text-center" title={creative.error || undefined}>
                        {isBusy
                          ? <Loader2 className="w-4 h-4 animate-spin text-warning" />
                          : creative.status === 'FAILED'
                            ? <AlertCircle className="w-4 h-4 text-red-400" />
                            : <ImageIcon className="w-4 h-4 text-text-disabled" />}
                        <span className="text-[9px] text-text-muted">
                          {isBusy ? 'Generowanie...' : creative.status === 'FAILED' ? 'Błąd' : 'Brak obrazu'}
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-start gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="text-xs font-medium text-text-primary">{creative.name}</span>
                          {creative.schemaNumber && (
                            <span className="text-[10px] text-text-muted">{creative.schemaNumber}{creative.schemaName ? ` ${creative.schemaName}` : ''}</span>
                          )}
                        </div>
                        <div className="flex items-center gap-1.5 flex-wrap mt-1">
                          {creative.funnelStage && (
                            <span className="px-1.5 py-0.5 rounded text-[9px] bg-surface-raised border border-border-subtle text-text-secondary">{creative.funnelStage}</span>
                          )}
                          <span className="px-1.5 py-0.5 rounded text-[9px] bg-surface-raised border border-border-subtle text-text-secondary">{creative.format}</span>
                          {creative.carouselGroup && (
                            <span className="px-1.5 py-0.5 rounded text-[9px] bg-info/10 border border-accent-info/20 text-accent-info">
                              Karuzela{creative.slideNumber ? ` - slajd ${creative.slideNumber}` : ''}
                            </span>
                          )}
                          {creative.variantLabel && (
                            <span className="px-1.5 py-0.5 rounded text-[9px] bg-accent-primary/10 border border-accent-primary/20 text-accent-primary">{creative.variantLabel}</span>
                          )}
                          <button
                            onClick={() => copyToClipboard(`sc-${creative.id}`, `[${creative.shortcode}]`)}
                            className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] bg-surface-raised border border-border-subtle text-text-secondary hover:text-accent-primary transition-colors"
                            title="Kopiuj shortcode"
                          >
                            {copiedKey === `sc-${creative.id}` ? <Check className="w-2.5 h-2.5 text-success" /> : <Hash className="w-2.5 h-2.5" />}
                            [{creative.shortcode}]
                          </button>
                        </div>
                        {creative.onImageText && (
                          <p className="text-[10px] text-text-secondary mt-1.5 line-clamp-2">
                            <span className="text-text-muted">Tekst na grafice:</span> {creative.onImageText}
                          </p>
                        )}
                      </div>

                      <div className="flex items-center gap-1 shrink-0">
                        {creative.status === 'READY' && creative.driveFileUrl && (
                          <a
                            href={creative.driveFileUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="p-1 rounded text-text-muted hover:text-accent-primary transition-colors"
                            title="Otwórz na Google Drive"
                            aria-label="Otwórz na Google Drive"
                          >
                            <ExternalLink className="w-3.5 h-3.5" />
                          </a>
                        )}
                        <button
                          onClick={() => startEdit(creative)}
                          disabled={isBusy}
                          className="p-1 rounded text-text-muted hover:text-accent-primary transition-colors disabled:opacity-40"
                          title="Edytuj prompt"
                          aria-label="Edytuj prompt"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => setDeleteTarget(creative)}
                          disabled={isBusy}
                          className="p-1 rounded text-text-muted hover:text-red-400 transition-colors disabled:opacity-40"
                          title="Usuń"
                          aria-label="Usuń kreację"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>

                    {creative.status === 'FAILED' && creative.error && (
                      <div className="flex items-start gap-1.5 mt-1.5 text-[10px] text-red-400">
                        <AlertCircle className="w-3 h-3 shrink-0 mt-0.5" /> {creative.error}
                      </div>
                    )}

                    {isEditing ? (
                      <div className="mt-2 space-y-2">
                        <textarea
                          value={editPrompt}
                          onChange={e => setEditPrompt(e.target.value)}
                          rows={6}
                          className="w-full text-[11px] bg-surface-base/60 border border-border rounded-lg px-2.5 py-2 text-text-primary focus:outline-none focus:border-accent-primary/40 font-mono"
                        />
                        <div className="flex items-center gap-2">
                          <button
                            onClick={saveEdit}
                            disabled={savingEdit || editPrompt.trim().length === 0}
                            className="flex items-center gap-1 px-3 py-1.5 text-[11px] font-medium text-white bg-accent-primary rounded-lg hover:bg-accent-primary/90 transition-all disabled:opacity-40"
                          >
                            {savingEdit ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                            Zapisz
                          </button>
                          <button
                            onClick={() => setEditingId(null)}
                            className="flex items-center gap-1 px-3 py-1.5 text-[11px] text-text-secondary hover:text-text-primary transition-colors"
                          >
                            <X className="w-3 h-3" /> Anuluj
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="mt-2 flex items-center gap-2 flex-wrap">
                        <button
                          onClick={() => handleGenerateOne(creative)}
                          disabled={isBusy}
                          className="flex items-center gap-1 px-2.5 py-1 text-[11px] font-medium text-accent-tertiary bg-accent-tertiary/10 border border-accent-tertiary/20 rounded-lg hover:bg-accent-tertiary/15 transition-all disabled:opacity-40"
                        >
                          {isBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : creative.status === 'READY' ? <Sparkles className="w-3 h-3" /> : <ImageIcon className="w-3 h-3" />}
                          {isBusy ? 'W kolejce...' : creative.status === 'READY' ? 'Przegeneruj obraz' : 'Generuj obraz'}
                        </button>
                        <button
                          onClick={() => copyToClipboard(`prompt-${creative.id}`, creative.prompt)}
                          className="flex items-center gap-1 px-2.5 py-1 text-[11px] text-text-secondary bg-surface-raised border border-border-subtle rounded-lg hover:text-text-primary transition-colors"
                          title="Kopiuj prompt"
                        >
                          {copiedKey === `prompt-${creative.id}` ? <Check className="w-3 h-3 text-success" /> : <Copy className="w-3 h-3" />}
                          Kopiuj prompt
                        </button>
                        <button
                          onClick={() => setExpandedPromptId(prev => (prev === creative.id ? null : creative.id))}
                          className="text-[11px] text-text-muted hover:text-text-primary transition-colors"
                        >
                          {promptExpanded ? 'Zwiń prompt' : 'Pokaż prompt'}
                        </button>
                        {creative.status === 'READY' && (
                          <span className="flex items-center gap-1 text-[10px] font-medium text-success ml-auto">
                            <CheckCircle className="w-3 h-3" /> Gotowa
                          </span>
                        )}
                      </div>
                    )}

                    {promptExpanded && !isEditing && (
                      <div className="mt-2 space-y-1.5">
                        <pre className="bg-surface-base/60 rounded-lg p-2.5 text-[10px] text-text-secondary whitespace-pre-wrap leading-relaxed max-h-56 overflow-y-auto font-mono">
                          {creative.prompt}
                        </pre>
                        {creative.executorNote && (
                          <p className="text-[10px] text-text-muted"><span className="font-medium">Notatka dla egzekutora:</span> {creative.executorNote}</p>
                        )}
                        {creative.hypothesis && (
                          <p className="text-[10px] text-text-muted"><span className="font-medium">Hipoteza:</span> {creative.hypothesis}</p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {lightbox && (
        <ImageLightbox src={lightbox.src} alt={lightbox.alt} onClose={() => setLightbox(null)} />
      )}

      <ConfirmDialog
        isOpen={Boolean(deleteTarget)}
        title="Usunąć kreację?"
        message={deleteTarget ? <>Czy na pewno chcesz usunąć <strong>{deleteTarget.name}</strong> ([{deleteTarget.shortcode}])? Shortcode przestanie działać.</> : ''}
        confirmLabel="Usuń"
        danger
        onConfirm={async () => {
          if (deleteTarget) await labApi.deleteAdCreative(productId, contentId, deleteTarget.id);
          setDeleteTarget(null);
          await loadCreatives();
        }}
        onClose={() => setDeleteTarget(null)}
      />
    </div>
  );
}
